import { useState, createElement } from 'react';
import { Heart, Users, Briefcase, GraduationCap, Wine } from 'lucide-react';
import { formatPhoneNumber } from './utils';
import { compressImage, compressImageForUpload } from './imageUtils';
import { uploadApi, getToken } from './api';
import { showToast } from './toast';
import ImageCropModal from '../components/ImageCropModal';

// ─── Constants ───────────────────────────────────────────────────────────────

export const RELATIONSHIP_TYPES = [
  { id: 'Family', icon: Heart, label: 'Family' },
  { id: 'Friend', icon: Users, label: 'Friend' },
  { id: 'Work', icon: Briefcase, label: 'Work' },
  { id: 'Mentor', icon: GraduationCap, label: 'Mentor' },
  { id: 'Date', icon: Wine, label: 'Date' },
];

export const COMMUNICATION_TYPES = [
  'phone',
  'email',
  'whatsapp',
  'messenger',
  'instagram',
  'linkedin',
  'twitter',
  'other',
];

export const SUGGESTED_TAGS = ['College', 'Work', 'Gym'];

// ─── Preferred Method Helpers ────────────────────────────────────────────────

/** Build a unique key for a communication method: "type:value" */
export function commKey(method: { type: string; value: string }): string {
  return `${method.type}:${method.value}`;
}

/** Check if a method is the preferred one. Handles legacy "type" and new "type:value" formats. */
export function isPreferred(
  preferred: string | undefined,
  method: { type: string; value: string },
): boolean {
  if (!preferred) return false;
  // New format: exact match on "type:value"
  if (preferred.includes(':')) return preferred === commKey(method);
  // Legacy format: match by type only
  return preferred === method.type;
}

// ─── Communication Methods Hook ─────────────────────────────────────────────

export function useCommMethods(
  initial: { type: string; value: string }[] = [],
  initialPreferred: string = '',
) {
  const [communicationMethods, setCommunicationMethods] =
    useState<{ type: string; value: string }[]>(initial);
  const [preferredCommMethod, setPreferredCommMethod] = useState(initialPreferred);
  const [commType, setCommType] = useState('phone');
  const [commValue, setCommValue] = useState('');

  const handleAdd = () => {
    if (!commValue.trim()) return;

    let finalValue = commValue.trim();
    if (commType === 'phone') {
      finalValue = formatPhoneNumber(finalValue);
    }

    const newMethod = { type: commType, value: finalValue };
    setCommunicationMethods((prev) => [...prev, newMethod]);
    if (communicationMethods.length === 0) {
      setPreferredCommMethod(commKey(newMethod));
    }
    setCommValue('');
  };

  const handleRemove = (index: number) => {
    const methodToRemove = communicationMethods[index];
    setCommunicationMethods((prev) => prev.filter((_, i) => i !== index));
    if (isPreferred(preferredCommMethod, methodToRemove)) {
      setPreferredCommMethod('');
    }
  };

  const handleReorder = (newOrder: { type: string; value: string }[]) => {
    setCommunicationMethods(newOrder);
  };

  const handleSetPreferred = (key: string) => {
    setPreferredCommMethod(key);
    // Move preferred method to top
    setCommunicationMethods((prev) => {
      const idx = prev.findIndex((m) => commKey(m) === key);
      if (idx <= 0) return prev;
      const item = prev[idx];
      return [item, ...prev.filter((_, i) => i !== idx)];
    });
  };

  return {
    communicationMethods,
    preferredCommMethod,
    setPreferredCommMethod: handleSetPreferred,
    commType,
    setCommType,
    commValue,
    setCommValue,
    handleAddCommMethod: handleAdd,
    handleRemoveCommMethod: handleRemove,
    handleReorder,
  };
}

// ─── Tags Hook ──────────────────────────────────────────────────────────────

export function useTags(initial: string[] = []) {
  const [selectedTags, setSelectedTags] = useState<string[]>(initial);
  const [customTag, setCustomTag] = useState('');
  const [isAddingTag, setIsAddingTag] = useState(false);

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const handleAddCustomTag = () => {
    const tag = customTag.trim();
    if (tag && !selectedTags.includes(tag)) {
      setSelectedTags((prev) => [...prev, tag]);
    }
    setCustomTag('');
    setIsAddingTag(false);
  };

  return {
    selectedTags,
    customTag,
    setCustomTag,
    isAddingTag,
    setIsAddingTag,
    toggleTag,
    handleAddCustomTag,
  };
}

// ─── Photo Upload ───────────────────────────────────────────────────────────

export function usePhotoUpload(
  setAvatar: (url: string) => void,
  target?: { type: 'user' | 'contact' | 'none'; id?: number },
) {
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);

  const uploadCropped = async (file: File) => {
    if (getToken()) {
      setUploading(true);
      try {
        const compressed = await compressImageForUpload(file);
        const { url } = await uploadApi.photo(compressed, target);
        setAvatar(url);
      } catch (err) {
        console.error('Failed to upload photo:', err);
        showToast('Failed to upload photo. Please try again.');
      } finally {
        setUploading(false);
      }
    } else {
      try {
        const compressedBase64 = await compressImage(file, 250, 250, 0.6);
        setAvatar(compressedBase64);
      } catch (err) {
        console.error('Failed to compress image:', err);
        const reader = new FileReader();
        reader.onloadend = () => setAvatar(reader.result as string);
        reader.readAsDataURL(file);
      }
    }
  };

  const handleFileChange = (file: File) => {
    const reader = new FileReader();
    reader.onload = (ev) => setCropSrc(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const cropModal = cropSrc
    ? createElement(ImageCropModal, {
        imageSrc: cropSrc,
        onCancel: () => setCropSrc(null),
        onCrop: (croppedFile: File) => {
          setCropSrc(null);
          uploadCropped(croppedFile);
        },
      })
    : null;

  return { uploading, handleFileChange, cropModal };
}
